import { ReactElement, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  InputBase,
  InputAdornment,
  CircularProgress,
  Button,
  Popover,
} from "@mui/material";
import { Check, Close, KeyboardArrowDown } from "@mui/icons-material";
import { CiSearch } from "react-icons/ci";
import { useGetAllSpOwnerPartnersQuery } from "@/services";
import { useAppSelector } from "@/store";
import { useDebounce } from "@/hooks";
import { FieldError } from "react-hook-form";
import { roles } from "@/roles";
interface IPartnerPickerProps extends React.ComponentPropsWithoutRef<"button"> {
  selectedPartner: SPOwnerPartnerTypes | null;
  setSelectedPartner: (partner: SPOwnerPartnerTypes | null) => void;
  error?: FieldError;
}
const PartnerPicker: React.FC<IPartnerPickerProps> = ({
  selectedPartner,
  setSelectedPartner,
  error,
  ...rest
}): ReactElement => {
  const { partners } = useAppSelector((state) => state.spOwnerPartner);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const user = useAppSelector((state) => state.auth.user);
  const isTDAdminSP = user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "SERVICE_PROVIDER";
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState("");
  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

  const openPartnerPickerMenu = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const closePartnerPickerMenu = () => {
    setAnchorEl(null);
    setSearch("");
  };

  function setActivePartner(partner: SPOwnerPartnerTypes) {
    setSelectedPartner(partner);
  }
  const handleDeselectPartner = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedPartner(null);
  };
  const filterPartners = useDebounce(setSearch, 1200);

  const { isFetching, isLoading } = useGetAllSpOwnerPartnersQuery(
    { searchKeyword: search ,...(isTDAdminSP && {tenantId:selectedTenant?._id})},
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const findPartner = (id: string) => {
    return partners.find((partner) => partner._id === id) || null;
  };

  useEffect(() => {
    if (rest.value && partners.length) {
      const valueAsString = String(rest.value);
      const foundPartner = findPartner(valueAsString);

      if (foundPartner && foundPartner !== selectedPartner) {
        setSelectedPartner(foundPartner);
      }
    }
  }, [rest.value, partners, selectedPartner]);

  return (
    <>
      <Button
        id="client-picker-anchor"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-controls={open ? "client-picker-menu" : undefined}
        sx={{
          justifyContent: "flex-start",
          border: error ? "1px solid #D32F2F" : "1px solid #045f5f",
          padding: 2,
          borderRadius: "8px",
          textAlign: "left",
          whiteSpace: "nowrap",
          minWidth: "initial",
          "&[aria-expanded='true']": {
            zIndex: 1500,
            border: {
              xs: "1px solid #0B494B",
              sm: 0,
            },
            boxShadow: {
              xs: "0px 0px 0px 4px #0B494B3D",
              sm: "0px 1px 2px 0px rgba(78, 78, 78, 0.05), 0px 0px 0px 4px rgba(11, 73, 75, 0.24)",
            },
          },
        }}
        onClick={openPartnerPickerMenu}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            width: "100%",
            gap: "10px",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: {
                xs: "none",
                sm: "initial",
              },
            }}
          >
            {selectedPartner ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#030712",
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "pre",
                  }}
                >
                  {selectedPartner.firstName + " " + selectedPartner.lastName}
                  <Typography
                    component="span"
                    sx={{ fontWeight: 400, color: "#8E9494" }}
                  >
                    ({selectedPartner.email})
                  </Typography>
                </Typography>

                {/* Cross icon to deselect partner */}
                <IconButton
                  size="small"
                  onClick={handleDeselectPartner}
                  sx={{ ml: 1 }}
                >
                  {isFetching || isLoading ? (
                    <CircularProgress size={20} sx={{ marginRight: 0.5 }} />
                  ) : (
                    <Close fontSize="small" />
                  )}
                </IconButton>
              </Stack>
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={0.5}
                sx={{
                  color: selectedPartner ? "#8E9494" : "#030712",
                  height: selectedPartner ? "14px" : "auto",
                }}
              >
                <Typography
                  sx={{
                    fontSize: selectedPartner ? 12 : 14,
                    fontWeight: selectedPartner ? 400 : 500,
                    color: "#8E9494",
                  }}
                >
                  Select Partner for the Client
                </Typography>
                <KeyboardArrowDown sx={{ color: "#8E9494", fontSize: 14 }} />
              </Stack>
            )}
          </Box>
        </Stack>
      </Button>

      <Popover
        sx={{
          "& .MuiPaper-root": {
            width: "556px",
            marginTop: 2,
            borderRadius: "4px",
            border: 0,
          },
        }}
        id="client-picker-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closePartnerPickerMenu}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: "10px" }}>
          <InputBase
            fullWidth
            sx={{
              backgroundColor: "#F1F2F3",
              border: "1px solid #EFEFEF",
              padding: "12px 1rem",
              borderRadius: "8px",
              mb: "5px",
              fontSize: "14px",
              fontFamily: "Plus Jakarta Sans",
              fontWeight: 500,
              lineHeight: "20px",
              "& .MuiInputBase-input": {
                height: "initial",
              },
            }}
            placeholder="Search"
            startAdornment={
              <InputAdornment position="start">
                <CiSearch size={18} />
              </InputAdornment>
            }
            endAdornment={
              isFetching ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : null
            }
            onChange={(e) => filterPartners(e.target.value)}
          />

          {!isFetching && partners.length === 0 ? (
            <Box py="5px" px="10px">
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Plus Jakarta Sans",
                }}
              >
                No Partners Founds
              </Typography>
            </Box>
          ) : (
            <Box
              className="client-list"
              sx={{
                maxHeight: 300,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: 3,
                },
                "&::-webkit-scrollbar-thumb": {
                  width: 3,
                  background: "#D9D9D9",
                  borderRadius: "1px",
                },
              }}
            >
              {partners.map((partner) => {
                const partnerName = partner.firstName + " " + partner.lastName;
                return (
                  <Stack
                    key={partner._id}
                    direction="row"
                    sx={{
                      padding: "5px 10px",
                      cursor: "pointer",
                      gap: 2,
                      alignItems: "center",
                    }}
                    onClick={() => {
                      setActivePartner(partner);
                      closePartnerPickerMenu();
                    }}
                  >
                    <Box>
                      <Typography
                        color="rgb(55, 65, 81)"
                        fontWeight={500}
                        fontSize={14}
                      >
                        {partnerName}
                      </Typography>
                      <Typography color="#8E9494" fontSize={12}>
                        {partner.email}
                      </Typography>
                    </Box>

                    {partner._id === selectedPartner?._id ? (
                      <IconButton
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          backgroundColor: "#0B494B",
                          marginLeft: "auto",
                        }}
                      >
                        <Check sx={{ width: 12, height: 12, color: "#fff" }} />
                      </IconButton>
                    ) : null}
                  </Stack>
                );
              })}
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default PartnerPicker;
